// utils/announcementsTime.js
// Robust ordering by timeAnnounced (primary), then BroadcastTime, then announcementDate.
// Handles: "DD-MMM-YYYY HH:mm", "HH:mm:ss", "HH:mm AM/PM", optional trailing TZ tokens.

const MONTH = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };

// normalize whitespace/dashes/commas
const normalize = (s) =>
    String(s || "")
        .replace(/\u00A0/g, " ")
        .replace(/[–—]/g, "-")
        .replace(/,\s*/g, " ")
        .replace(/\s+/g, " ")
        .trim();

// "DD-MMM-YYYY" (or 2-digit year) -> { y, mon, d } | null
function parseDMY(dmy) {
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{2}|\d{4})$/.exec(dmy);
    if (!m) return null;
    const d = +m[1];
    const mon = MONTH[m[2]];
    if (mon == null) return null;
    let y = +m[3];
    if (y < 100) y += y >= 70 ? 1900 : 2000; // tolerate 2-digit years
    return { y, mon, d };
}

// "YYYY-MM-DD" or "YYYYMMDD" -> { y, mon, d } | null
function parseYMD(s) {
    let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
    if (!m) return null;
    return { y: +m[1], mon: +m[2] - 1, d: +m[3] };
}

// "HH:mm" | "HH:mm:ss" | "H:mm AM" | "H:mm:ss pm" -> {H,M,S,hasTime}
function parseTimePart(t) {
    if (!t) return { H: 0, M: 0, S: 0, hasTime: false };
    const s = normalize(t);
    // split possible AM/PM
    const ampmMatch = /^(.*?)(?:\s*(AM|PM))?$/i.exec(s);
    let core = normalize(ampmMatch[1]);
    const ampm = ampmMatch[2] ? ampmMatch[2].toUpperCase() : null;

    const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(core);
    if (!m) return { H: 0, M: 0, S: 0, hasTime: false };

    let H = +m[1], M = +m[2], S = m[3] ? +m[3] : 0;
    if (ampm) {
        if (ampm === "AM") { if (H === 12) H = 0; }
        else if (ampm === "PM") { if (H !== 12) H += 12; }
    }
    return { H, M, S, hasTime: true };
}

// Parse a "DD-MMM-YYYY [time...]" style string.
// Returns { epochMs, hasTime } or null if not recognizable.
function parseDateTimeFlexible(s) {
    if (!s) return null;
    const str = normalize(s);
    const parts = str.split(" "); // date [time [tz]]
    if (parts.length === 0) return null;

    const datePart = parts[0];
    let dmy = parseDMY(datePart);
    if (!dmy) {
        const ymd = parseYMD(datePart);
        if (ymd) dmy = { y: ymd.y, mon: ymd.mon, d: ymd.d };
    }
    if (!dmy) return null;

    // Reconstitute time part (if any) and allow "time TZ"
    let timePart = parts.slice(1).join(" ").trim();
    // drop trailing pure letters like "IST" if present
    timePart = timePart.replace(/\b[A-Z]{2,}\b$/i, "").trim();

    const t = parseTimePart(timePart);
    const epochMs = Date.UTC(dmy.y, dmy.mon, dmy.d, t.H, t.M, t.S, 0);
    return { epochMs, hasTime: t.hasTime };
}

/**
 * Parse an announcement record to { epochMs, hasTime } with priority:
 *   1) timeAnnounced    (full timestamp)
 *   2) BroadcastTime    (full timestamp)
 *   3) announcementDate (date only; treated as 00:00 of that day)
 */
export function parseFromRecord(a) {
    // 1) primary
    const p1 = parseDateTimeFlexible(a?.timeAnnounced);
    if (p1) return p1;

    // 2) secondary
    const p2 = parseDateTimeFlexible(a?.BroadcastTime);
    if (p2) return p2;

    // 3) fallback to date-only
    const d = normalize(a?.announcementDate);
    let dm = parseDMY(d);
    if (!dm) {
        const ymd2 = parseYMD(d);
        if (ymd2) dm = { y: ymd2.y, mon: ymd2.mon, d: ymd2.d };
    }
    if (dm) {
        const epochMs = Date.UTC(dm.y, dm.mon, dm.d, 0, 0, 0, 0);
        return { epochMs, hasTime: false };
    }

    // No usable date; push to bottom safely
    return { epochMs: 0, hasTime: false };
}

export function compareAnnouncementsDesc(a, b) {
    const pa = parseFromRecord(a);
    const pb = parseFromRecord(b);

    if (pb.epochMs !== pa.epochMs) return pb.epochMs - pa.epochMs; // newer first
    if (pb.hasTime !== pa.hasTime) return pb.hasTime ? -1 : 1;     // same day: with time first

    // Stable tiebreakers (prefer descending numeric id if both numeric)
    const ida = a?.id, idb = b?.id;
    const na = Number.isFinite(+ida) ? +ida : null;
    const nb = Number.isFinite(+idb) ? +idb : null;
    if (na != null && nb != null && nb !== na) return nb - na;

    return String(ida ?? a?.message ?? "").localeCompare(String(idb ?? b?.message ?? ""));
}

/* --- helpers you already consume --- */

export const formatLocalDate = (d) => {
    if (!d) return null;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    if (typeof d === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
            const [y, m, day] = d.split("-").map(Number);
            return `${day} ${months[m - 1]}, ${y}`;
        }
        if (/^\d{8}$/.test(d)) {
            const y = Number(d.slice(0, 4));
            const m = Number(d.slice(4, 6));
            const day = Number(d.slice(6, 8));
            return `${day} ${months[m - 1]}, ${y}`;
        }
    } else if (typeof d === "object" && d.year && d.month && d.day) {
        return `${d.day} ${months[d.month - 1]}, ${d.year}`;
    }
    const dt = new Date(d);
    if (!isNaN(dt)) return `${dt.getDate()} ${months[dt.getMonth()]}, ${dt.getFullYear()}`;
    return String(d);
};

export const normYYYYMMDD = (d) => {
    if (!d) return null;
    if (typeof d === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d.replace(/-/g, "");
        if (/^\d{8}$/.test(d)) return d;
        const dm = parseDMY(d);
        if (dm) {
            const mm = String(dm.mon + 1).padStart(2, "0");
            const dd = String(dm.d).padStart(2, "0");
            return `${dm.y}${mm}${dd}`;
        }
    } else if (typeof d === "object" && d.year && d.month && d.day) {
        const mm = String(d.month).padStart(2, "0");
        const dd = String(d.day).padStart(2, "0");
        return `${d.year}${mm}${dd}`;
    }
    const dt = new Date(d);
    if (!isNaN(dt)) {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const day = String(dt.getDate()).padStart(2, "0");
        return `${y}${m}${day}`;
    }
    return null;
};

export const idOf = (a) => (a && a.id != null ? String(a.id) : null);

export const badgeInfo = (alertType) => {
    switch (alertType) {
        case "FIRST_EVER_CONCALL":     return { text: "First-ever concall", cls: "badge-concall" };
        case "ONE_YEAR_LATER_CONCALL": return { text: "1-yr-later concall", cls: "badge-concall" };
        case "FIRST_EVER_PPT":         return { text: "First-ever PPT",     cls: "badge-ppt"     };
        case "ONE_YEAR_LATER_PPT":     return { text: "1-yr-later PPT",     cls: "badge-ppt"     };
        default: return null;
    }
};
